/**
 * This module is responsible for encoding and decoding the loaded states of the app.
 * In viewpoint mode, each dataset is represented by a LoadedState object.
 *
 * As these are encoded and stored in the URL, we need to ensure backwards compatibility
 * by making sure encoding and decoding is versioned correctly.
 *
 * When making changes to the encoding/decoding, don't change existing versions -
 * instead:
 * - create a new version by copying the latest existing versions folder
 * - make changes and update tests for the new version
 * - add the new version to SUPPORTED_VERSIONS in versions.ts
 * - import the new decode function in this file and add it to the decoderVersionMap
 * - change the import for the encoder to the new version in this file  (This is what switches over to use the new version)
 * - update the if-statement checking version for tracking use of old versions (in this file)
 * - consider if it might be time to remove support for old versions
 */

import { logError } from '@ardoq/logging';
import { decodeLoadedStateV1 } from './encodeDecode.v1/decode';
import { decodeLoadedStateV2 } from './encodeDecode.v2/decode';
import { decodeLoadedStateV3 } from './encodeDecode.v3/decode';
import { decodeLoadedStateV4 } from './encodeDecode.v4/decode';
import { decodeLoadedStateV5 } from './encodeDecode.v5/decode';
import { decodeLoadedStateV6 } from './encodeDecode.v6/decode';
import { decodeLoadedStateV7 } from './encodeDecode.v7/decode';
import { decodeLoadedStateV8 } from './encodeDecode.v8/decode';
import { decodeLoadedStateV9 } from './encodeDecode.v9/decode';
import { decodeLoadedStateV10 } from './encodeDecode.v10/decode';
import { decodeLoadedStateV11 } from './encodeDecode.v11/decode';
import { decodeLoadedStateV12 } from './encodeDecode.v12/decode';
import { decodeLoadedStateV13 } from './encodeDecode.v13/decode';
import { encodeLoadedStateV13 as currentVersionEncoder } from './encodeDecode.v13/encode';
import { STATE_SEPARATOR } from './consts';
import { isSupportedVersion, SupportedVersions } from './versions';
import { PersistedLoadedStateParams } from '@ardoq/api-types';
import { LoadedStateParams as LegacyLoadedStateParams } from './encodeDecode.legacy/componentSelectionTypes';
import { legacyStateToCanonical } from './encodeDecode.legacy/adapter';
import { loadedStateTracking } from './loadedStateTracking';

// enforce that all supported versions have a decoder mapping
type DecoderVersionMap = Record<
  SupportedVersions,
  (encodedLoadedState: string) => PersistedLoadedStateParams[]
>;

// The v1–v9 codecs are frozen against the legacy ComponentSelection shape (see
// ./encodeDecode.legacy). This wraps a legacy decoder so it emits the canonical
// shape the rest of the app consumes.
const adaptLegacy =
  (decode: (encodedLoadedState: string) => LegacyLoadedStateParams[]) =>
  (encodedLoadedState: string): PersistedLoadedStateParams[] =>
    decode(encodedLoadedState).map(legacyStateToCanonical);

const decoderVersionMap: DecoderVersionMap = {
  v1: adaptLegacy(decodeLoadedStateV1),
  v2: adaptLegacy(decodeLoadedStateV2),
  v3: adaptLegacy(decodeLoadedStateV3),
  v4: adaptLegacy(decodeLoadedStateV4),
  v5: adaptLegacy(decodeLoadedStateV5),
  v6: adaptLegacy(decodeLoadedStateV6),
  v7: adaptLegacy(decodeLoadedStateV7),
  v8: adaptLegacy(decodeLoadedStateV8),
  v9: adaptLegacy(decodeLoadedStateV9),
  v10: decodeLoadedStateV10,
  v11: decodeLoadedStateV11,
  v12: decodeLoadedStateV12,
  v13: decodeLoadedStateV13,
};

// regex to split on first instance of STATE_SEPARATOR to extract version from encoded state
const reSplitVersion = new RegExp(String.raw`(?<=^v\d+)${STATE_SEPARATOR}`);

export const splitVersionAndState = (
  encodedStates: string
): { version: string; state: string } => {
  const [version, state] = encodedStates.split(reSplitVersion);
  return { version, state };
};

export const decodeLoadedState = (
  encodedStates: string
): PersistedLoadedStateParams[] => {
  if (encodedStates === '') return [];
  const { version, state } = splitVersionAndState(encodedStates);
  try {
    if (!isSupportedVersion(version)) {
      logError(Error('Unhandled version in loaded state'));
      return [];
    }
    if (version !== 'v13') {
      loadedStateTracking.trackOldVersion(version);
    }
    // Execute mapped decoder
    const decoder = decoderVersionMap[version];
    return decoder(state);
  } catch (e) {
    logError(Error('Decoding loaded state failed'));
    return [];
  }
};

export const encodeLoadedState = (
  states: PersistedLoadedStateParams[]
): string => {
  if (states.length === 0) return '';
  try {
    // Always encode with the latest encoder.
    return currentVersionEncoder(states);
  } catch (e) {
    logError(Error('Encoding loaded state failed'));
    return '';
  }
};

export const haveSameEncodedLoadedState = (
  states: PersistedLoadedStateParams[],
  otherStates: PersistedLoadedStateParams[]
): boolean => encodeLoadedState(states) === encodeLoadedState(otherStates);
